import React, { useState, useEffect } from 'react';
import './Calculator.css';

import { Heading } from '../../../components';

import { FaRulerCombined, FaLayerGroup, FaCaretSquareUp, FaBuilding, FaSun, FaAngleDown } from 'react-icons/fa';
import { FaHouseChimney } from 'react-icons/fa6';



const CostCalculator = () => {

    // Define costs for different roof materials
    const roofingCosts = {
        asphalt: 3, // cost per square foot
        metal: 5,
        tile: 6,
        wood: 4,
        other: 2
    };

    // Define multipliers for different roof pitches
    const roofPitchMultiplier = {
        low: 1,
        medium: 1.2,
        high: 1.5
    };

    // Define costs for extra services
    const extraServicesCost = {
        installation: 2.5, // cost per square foot
        removal: 1.5,
        gutter: 0.75
    };

    // State for form inputs
    const [formData, setFormData] = useState({
        roofArea: '',
        roofMaterial: '',
        roofPitch: '',
        numStories: '',
        numChimneys: '',
        numSkylights: ''
    });

    const [services, setServices] = useState({
        installation: false,
        removal: false,
        gutter: false
    });

    const [totalCost, setTotalCost] = useState(0);

    // Handle input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Handle checkbox changes
    const handleServiceChange = (e) => {
        const { name, checked } = e.target;
        setServices(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    // Calculate cost whenever inputs change
    useEffect(() => {
        calculateCost();
    }, [formData, services]);

    const calculateCost = () => {

        // Retrieve input values
        const roofArea = parseFloat(formData.roofArea);
        const roofMaterial = formData.roofMaterial;
        const roofPitch = formData.roofPitch;
        const numStories = parseInt(formData.numStories) || 1; // Default to 1 story if not provided
        const numChimneys = parseInt(formData.numChimneys) || 0; // Default to 0 chimneys if not provided
        const numSkylights = parseInt(formData.numSkylights) || 0; // Default to 0 skylights if not provided

        // Calculate base cost (only if roofArea and roofMaterial are provided)
        let baseCost = 0;
        if (roofArea && roofMaterial) {
            const pitchMultiplier = roofPitchMultiplier[roofPitch] || 1; // Default to 'low' pitch if not selected
            baseCost = roofArea * roofingCosts[roofMaterial] * pitchMultiplier * numStories;
        }

        // Calculate extra costs for chimneys and skylights
        let extraCost = (numChimneys * 100) + (numSkylights * 150);

        // Additional service costs (add only if checked)
        if (roofArea) {
            if (services.installation) {
                extraCost += roofArea * extraServicesCost.installation;
            }
            if (services.removal) {
                extraCost += roofArea * extraServicesCost.removal;
            }
            if (services.gutter) {
                extraCost += roofArea * extraServicesCost.gutter;
            }
        }

        // Calculate total cost
        const calculatedCost = baseCost + extraCost;
        setTotalCost(calculatedCost.toFixed(2));

    };

    return (
        <section className="custom-form cost-calculator linear-bg">
            <div className="box-container"> 
                <div className="content"> 
                    <form method="post" className="calculator-form dark-form"> 

                        {/* Section heading */}
                        <Heading title={'Cost Calculator'} subTitle={'Estimate Your Roofing Costs'} linear sideHeading />

                        {/* Roof Area Input */}
                        <div className="input-box">
                            <div className="input-field">
                                <label htmlFor="roof-area"><FaRulerCombined /></label>
                                <input 
                                    type="number" 
                                    name="roofArea" 
                                    className="box" 
                                    id="roof-area" 
                                    min="0" 
                                    placeholder="Roof Area (sq ft)" 
                                    value={formData.roofArea}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Roof Material Dropdown */}
                        <div className="input-box">
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="roof-material"><FaLayerGroup /></label>
                                    <select 
                                        className="select-box" 
                                        id="roof-material" 
                                        name="roofMaterial" 
                                        value={formData.roofMaterial}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Roof Material</option>
                                        <option value="asphalt">Asphalt Shingles</option>
                                        <option value="metal">Metal Roofing</option>
                                        <option value="tile">Tile Roofing</option>
                                        <option value="wood">Wood Shingles</option>
                                        <option value="other">Other</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                        </div> 

                        {/* Roof Pitch Dropdown */}
                        <div className="input-box">
                            <div className="select">
                                <div className="select-container">
                                    <label htmlFor="roof-pitch"><FaCaretSquareUp /></label>
                                    <select 
                                        className="select-box" 
                                        id="roof-pitch" 
                                        name="roofPitch" 
                                        value={formData.roofPitch}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Roof Pitch</option>
                                        <option value="low">Low Pitch</option>
                                        <option value="medium">Medium Pitch</option>
                                        <option value="high">High Pitch</option>
                                    </select>
                                    <div className="icon-container">
                                        <FaAngleDown className="icon" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Number of Stories Input */}
                        <div className="input-box">
                            <div className="input-field">
                                <label htmlFor="num-stories"><FaBuilding /></label>
                                <input 
                                    type="number" 
                                    name="numStories" 
                                    className="box" 
                                    id="num-stories" 
                                    min="1" 
                                    placeholder="Number of Stories" 
                                    value={formData.numStories}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Number of Chimneys Input */}
                        <div className="input-box">
                            <div className="input-field">
                                <label htmlFor="num-chimneys"><FaHouseChimney /></label>
                                <input 
                                    type="number" 
                                    name="numChimneys" 
                                    className="box" 
                                    id="num-chimneys" 
                                    min="0" 
                                    placeholder="Number of Chimneys" 
                                    value={formData.numChimneys}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Number of Skylights Input */}
                        <div className="input-box">
                            <div className="input-field">
                                <label htmlFor="num-skylights"><FaSun /></label>
                                <input 
                                    type="number" 
                                    name="numSkylights" 
                                    className="box" 
                                    id="num-skylights" 
                                    min="0" 
                                    placeholder="Number of Skylights" 
                                    value={formData.numSkylights}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Extra Services Checkboxes */}
                        <div className="extra-services">

                            {/* Installation checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="installation-checkbox" 
                                    name="installation"
                                    checked={services.installation}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="installation-checkbox">Installation</label>
                                <div className="checkbox-indicator"></div>
                            </div>

                            {/* Roof Removal checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="removal-checkbox" 
                                    name="removal"
                                    checked={services.removal}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="removal-checkbox">Roof Removal</label>
                                <div className="checkbox-indicator"></div>
                            </div>

                            {/* Gutter Installation checkbox */}
                            <div className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    id="gutter-checkbox" 
                                    name="gutter"
                                    checked={services.gutter}
                                    onChange={handleServiceChange}
                                />
                                <label htmlFor="gutter-checkbox">Gutter Installation</label>
                                <div className="checkbox-indicator"></div>
                            </div>

                        </div>

                        {/* Total Cost Display */}
                        <div className="cost-total">
                            <h5>Total Cost</h5>
                            <span>${totalCost}</span>
                        </div>

                    </form>

                </div>
            </div>

        </section>
    );
};

export default CostCalculator;